import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  approveRegistrationRequest,
  allowProductActivation,
  adminAllowStation,
  adminBanStation,
  clearAdminAllowStation,
  clearAdminBanStation,
	createAuthorityMonitoringTarget,
	createLocalMonitoringIntent,
	createLocalCustomer,
  createRelease,
  createReleaseDownloadGrant,
  deleteRelease,
  deleteReleaseArtifact,
  decommissionStation,
  getOverrideUsageReport,
  getClientLicensing,
	getAuthorityCustomer,
	getTenantSession,
  getPublicKey,
  getRegistrationRequest,
  getRolloutReadinessReport,
  getStation,
  getStationLicenseState,
  issueEnrollmentToken,
  issueStationOverride,
  listApps,
  listAudit,
  listEnrollmentTokens,
  listCustomerDirectory,
	listAuthorityMonitoringTargets,
	listAuthorityMonitoringBindings,
  listProductActivations,
  listReleaseArtifacts,
  listReleaseDownloadGrants,
  listReleases,
  listStationAudit,
  listStationCerts,
  listStationOverrides,
  listStations,
  publishRelease,
  resolveRegistrationRequest,
  rejectProductActivation,
  restoreRelease,
  revokeReleaseDownloadGrant,
	revokeAuthorityMonitoringBinding,
  revokeClientProductEntitlement,
  revokeProductActivation,
  saveApp,
  suspendClientProductEntitlement,
  updateStationPolicy,
	updateAuthorityMonitoringTarget,
	updateLocalCustomer,
  uploadReleaseArtifact,
  yankRelease
} from "@/api/admin-api";
import type {
  CreateReleaseInput,
	CreateMonitoringTargetInput,
	CreateLocalMonitoringIntentInput,
  ClearStationAdminControlInput,
  CreateReleaseDownloadGrantInput,
  DeleteReleaseArtifactInput,
  DecommissionStationInput,
  IssueEnrollmentTokenInput,
  IssueStationOverrideInput,
  MonitoringTargetUpdateInput,
	RevokeMonitoringBindingInput,
	LocalCustomerMutationInput,
  ProductActivationMutationInput,
  ProductEntitlementMutationInput,
  RegistrationRequestApprovalInput,
  RegistrationRequestResolveInput,
  RestoreReleaseInput,
  RevokeReleaseDownloadGrantInput,
  SaveAppInput,
  StationAdminControlInput,
  UpdateStationPolicyInput,
  UploadReleaseArtifactInput,
  YankReleaseInput
} from "@/api/types";

export function useCustomerDirectoryQuery(input: { q?: string; mode: "footprint" | "all"; limit?: number }, enabled = true) {
	return useInfiniteQuery({
		queryKey: ["customer-directory", input.mode, input.q?.trim() ?? "", input.limit ?? 24],
		queryFn: ({ pageParam }) => listCustomerDirectory({ ...input, cursor: pageParam }),
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (page) => page.nextCursor,
		enabled
	});
}

export function useTenantSessionQuery(enabled = true) {
	return useQuery({ queryKey: ["tenant-session"], queryFn: getTenantSession, staleTime: 60_000, enabled });
}

export function useCreateLocalCustomerMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: LocalCustomerMutationInput) => createLocalCustomer(input),
		onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["customer-directory"] })
	});
}

export function useUpdateLocalCustomerMutation(clientId: number) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: LocalCustomerMutationInput & { clientId: number; status: "ACTIVE" | "SUSPENDED" | "ARCHIVED"; expectedRevision: number }) => updateLocalCustomer(input),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ["authority-customer", clientId] });
			void queryClient.invalidateQueries({ queryKey: ["customer-directory"] });
		}
	});
}

export function useAuthorityCustomerQuery(clientId: number) {
	return useQuery({
		queryKey: ["authority-customer", clientId],
		queryFn: () => getAuthorityCustomer(clientId),
		enabled: Number.isSafeInteger(clientId) && clientId > 0,
		staleTime: 60_000
	});
}

export function useMonitoringTargetsQuery(
  clientId: number,
  view: "all" | "attached" | "unbound" | "archived",
  enabled = true
) {
  return useInfiniteQuery({
    queryKey: ["monitoring-targets", clientId, view],
	queryFn: ({ pageParam }) => listAuthorityMonitoringTargets(clientId, view, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.next_cursor ?? undefined,
    enabled: enabled && Number.isInteger(clientId) && clientId > 0
  });
}

export function useMonitoringBindingsQuery(clientId: number, targetId: string, enabled = true) {
	return useInfiniteQuery({
		queryKey: ["monitoring-bindings", clientId, targetId],
		queryFn: ({ pageParam }) => listAuthorityMonitoringBindings(clientId, targetId, pageParam),
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (page) => page.next_cursor ?? undefined,
		enabled: enabled && Number.isInteger(clientId) && clientId > 0 && targetId.length > 0
	});
}

export function useRevokeMonitoringBindingMutation(clientId: number, targetId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: RevokeMonitoringBindingInput) => revokeAuthorityMonitoringBinding(clientId, input),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ["monitoring-bindings", clientId, targetId] });
			void queryClient.invalidateQueries({ queryKey: ["monitoring-targets", clientId] });
		}
	});
}

export function useCreateLocalMonitoringIntentMutation(clientId: number) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: CreateLocalMonitoringIntentInput) => createLocalMonitoringIntent(input),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ["monitoring-targets", clientId] });
			void queryClient.invalidateQueries({ queryKey: ["authority-customer", clientId] });
			void queryClient.invalidateQueries({ queryKey: ["customer-directory"] });
		}
	});
}

export function useUpdateMonitoringTargetMutation(clientId: number) {
  const queryClient = useQueryClient();
  return useMutation({
	mutationFn: (input: MonitoringTargetUpdateInput) => updateAuthorityMonitoringTarget(clientId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["monitoring-targets", clientId] });
    }
  });
}

export function useCreateMonitoringTargetMutation(clientId: number) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: CreateMonitoringTargetInput) => createAuthorityMonitoringTarget(input),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ["monitoring-targets", clientId] });
			void queryClient.invalidateQueries({ queryKey: ["authority-customer", clientId] });
			void queryClient.invalidateQueries({ queryKey: ["customer-directory"] });
		}
	});
}

export function useClientLicensingQuery(clientId: number) {
  return useQuery({
    queryKey: ["client-licensing", clientId],
    queryFn: () => getClientLicensing(clientId),
    enabled: Number.isInteger(clientId) && clientId > 0
  });
}

export function useProductActivationsQuery(params: URLSearchParams) {
  return useQuery({
    queryKey: ["product-activations", params.toString()],
    queryFn: () => listProductActivations(params)
  });
}

function useProductActivationMutation(mutationFn: (input: ProductActivationMutationInput) => Promise<unknown>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (_result, input) => {
      void queryClient.invalidateQueries({ queryKey: ["client-licensing", input.clientId] });
      void queryClient.invalidateQueries({ queryKey: ["product-activations"] });
      void queryClient.invalidateQueries({ queryKey: ["audit"] });
    }
  });
}

export function useAllowProductActivationMutation() {
  return useProductActivationMutation(allowProductActivation);
}

export function useRejectProductActivationMutation() {
  return useProductActivationMutation(rejectProductActivation);
}

export function useRevokeProductActivationMutation() {
  return useProductActivationMutation(revokeProductActivation);
}

function useProductEntitlementMutation(mutationFn: (input: ProductEntitlementMutationInput) => Promise<unknown>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (_result, input) => {
      void queryClient.invalidateQueries({ queryKey: ["client-licensing", input.clientId] });
      void queryClient.invalidateQueries({ queryKey: ["product-activations"] });
      void queryClient.invalidateQueries({ queryKey: ["audit"] });
    }
  });
}

export function useSuspendClientProductEntitlementMutation() {
  return useProductEntitlementMutation(suspendClientProductEntitlement);
}

export function useRevokeClientProductEntitlementMutation() {
  return useProductEntitlementMutation(revokeClientProductEntitlement);
}

export function useAppsQuery() {
  return useQuery({
    queryKey: ["apps"],
    queryFn: listApps
  });
}

export function useSaveAppMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SaveAppInput) => saveApp(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["apps"] });
    }
  });
}

export function useReleasesQuery(params: URLSearchParams) {
  return useQuery({
    queryKey: ["releases", params.toString()],
    queryFn: () => listReleases(params)
  });
}

export function useCreateReleaseMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateReleaseInput) => createRelease(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["releases"] });
      void queryClient.invalidateQueries({ queryKey: ["audit"] });
    }
  });
}

export function useDeleteReleaseMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (releaseId: string) => deleteRelease(releaseId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["releases"] });
      void queryClient.invalidateQueries({ queryKey: ["audit"] });
    }
  });
}

export function useReleaseArtifactsQuery(releaseId: string) {
  return useQuery({
    queryKey: ["release-artifacts", releaseId],
    queryFn: () => listReleaseArtifacts(releaseId),
    enabled: releaseId.length > 0
  });
}

export function useUploadReleaseArtifactMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UploadReleaseArtifactInput) => uploadReleaseArtifact(input),
    onSuccess: (_artifact, input) => {
      void queryClient.invalidateQueries({ queryKey: ["release-artifacts", input.releaseId] });
      void queryClient.invalidateQueries({ queryKey: ["audit"] });
    }
  });
}

export function useDeleteReleaseArtifactMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: DeleteReleaseArtifactInput) => deleteReleaseArtifact(input),
    onSuccess: (_artifact, input) => {
      void queryClient.invalidateQueries({ queryKey: ["release-artifacts", input.releaseId] });
      void queryClient.invalidateQueries({ queryKey: ["audit"] });
    }
  });
}

export function useReleaseDownloadGrantsQuery(params: URLSearchParams, enabled = true) {
  return useQuery({
    queryKey: ["release-download-grants", params.toString()],
    queryFn: () => listReleaseDownloadGrants(params),
    enabled
  });
}

export function useCreateReleaseDownloadGrantMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateReleaseDownloadGrantInput) => createReleaseDownloadGrant(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["release-download-grants"] });
      void queryClient.invalidateQueries({ queryKey: ["audit"] });
    }
  });
}

export function useRevokeReleaseDownloadGrantMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RevokeReleaseDownloadGrantInput) => revokeReleaseDownloadGrant(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["release-download-grants"] });
      void queryClient.invalidateQueries({ queryKey: ["audit"] });
    }
  });
}

export function usePublishReleaseMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (releaseId: string) => publishRelease(releaseId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["releases"] });
      void queryClient.invalidateQueries({ queryKey: ["audit"] });
    }
  });
}

export function useYankReleaseMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: YankReleaseInput) => yankRelease(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["releases"] });
      void queryClient.invalidateQueries({ queryKey: ["audit"] });
    }
  });
}

export function useRestoreReleaseMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RestoreReleaseInput) => restoreRelease(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["releases"] });
      void queryClient.invalidateQueries({ queryKey: ["audit"] });
    }
  });
}

export function useEnrollmentTokensQuery(params: URLSearchParams, enabled = true) {
  return useQuery({
    queryKey: ["enrollment-tokens", params.toString()],
    queryFn: () => listEnrollmentTokens(params),
    enabled
  });
}

export function useIssueEnrollmentTokenMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: IssueEnrollmentTokenInput) => issueEnrollmentToken(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["enrollment-tokens"] });
    }
  });
}

export function useResolveRegistrationRequestMutation() {
  return useMutation({
    mutationFn: (input: RegistrationRequestResolveInput) => resolveRegistrationRequest(input)
  });
}

export function useApproveRegistrationRequestMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RegistrationRequestApprovalInput) => approveRegistrationRequest(input),
    onSuccess: (result, input) => {
      queryClient.setQueryData(["registration-request", input.requestId], result);
      void queryClient.invalidateQueries({ queryKey: ["stations"] });
      void queryClient.invalidateQueries({ queryKey: ["client-licensing", input.clientId] });
      void queryClient.invalidateQueries({ queryKey: ["audit"] });
    }
  });
}

export function useRegistrationRequestQuery(requestId: string, enabled = true) {
  return useQuery({
    queryKey: ["registration-request", requestId],
    queryFn: () => getRegistrationRequest(requestId),
    enabled: enabled && requestId.length > 0
  });
}

export function useStationsQuery(params: URLSearchParams, enabled = true) {
  return useQuery({
    queryKey: ["stations", params.toString()],
    queryFn: () => listStations(params),
    enabled
  });
}

export function useStationQuery(stationId: string) {
  return useQuery({
    queryKey: ["station", stationId],
    queryFn: () => getStation(stationId),
    enabled: stationId.length > 0
  });
}

export function useStationLicenseStateQuery(stationId: string) {
  return useQuery({
    queryKey: ["station-license-state", stationId],
    queryFn: () => getStationLicenseState(stationId),
    enabled: stationId.length > 0
  });
}

export function useStationCertsQuery(stationId: string) {
  return useQuery({
    queryKey: ["station-certs", stationId],
    queryFn: () => listStationCerts(stationId),
    enabled: stationId.length > 0
  });
}

export function useStationOverridesQuery(stationId: string) {
  return useQuery({
    queryKey: ["station-overrides", stationId],
    queryFn: () => listStationOverrides(stationId),
    enabled: stationId.length > 0
  });
}

export function useIssueStationOverrideMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: IssueStationOverrideInput) => issueStationOverride(input),
    onSuccess: (_override, input) => {
      void queryClient.invalidateQueries({ queryKey: ["station-overrides", input.stationId] });
      void queryClient.invalidateQueries({ queryKey: ["station-audit", input.stationId] });
      void queryClient.invalidateQueries({ queryKey: ["station-license-state", input.stationId] });
    }
  });
}

export function useDecommissionStationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: DecommissionStationInput) => decommissionStation(input),
    onSuccess: (_station, input) => {
      void queryClient.invalidateQueries({ queryKey: ["station", input.stationId] });
      void queryClient.invalidateQueries({ queryKey: ["station-license-state", input.stationId] });
      void queryClient.invalidateQueries({ queryKey: ["station-audit", input.stationId] });
      void queryClient.invalidateQueries({ queryKey: ["stations"] });
    }
  });
}

export function useAdminBanStationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: StationAdminControlInput) => adminBanStation(input),
    onSuccess: (_station, input) => {
      void queryClient.invalidateQueries({ queryKey: ["station", input.stationId] });
      void queryClient.invalidateQueries({ queryKey: ["station-license-state", input.stationId] });
      void queryClient.invalidateQueries({ queryKey: ["station-audit", input.stationId] });
      void queryClient.invalidateQueries({ queryKey: ["stations"] });
    }
  });
}

export function useAdminAllowStationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: StationAdminControlInput) => adminAllowStation(input),
    onSuccess: (_station, input) => {
      void queryClient.invalidateQueries({ queryKey: ["station", input.stationId] });
      void queryClient.invalidateQueries({ queryKey: ["station-license-state", input.stationId] });
      void queryClient.invalidateQueries({ queryKey: ["station-audit", input.stationId] });
      void queryClient.invalidateQueries({ queryKey: ["stations"] });
    }
  });
}

export function useClearAdminBanStationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ClearStationAdminControlInput) => clearAdminBanStation(input),
    onSuccess: (_station, input) => {
      void queryClient.invalidateQueries({ queryKey: ["station", input.stationId] });
      void queryClient.invalidateQueries({ queryKey: ["station-license-state", input.stationId] });
      void queryClient.invalidateQueries({ queryKey: ["station-audit", input.stationId] });
      void queryClient.invalidateQueries({ queryKey: ["stations"] });
    }
  });
}

export function useClearAdminAllowStationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ClearStationAdminControlInput) => clearAdminAllowStation(input),
    onSuccess: (_station, input) => {
      void queryClient.invalidateQueries({ queryKey: ["station", input.stationId] });
      void queryClient.invalidateQueries({ queryKey: ["station-license-state", input.stationId] });
      void queryClient.invalidateQueries({ queryKey: ["station-audit", input.stationId] });
      void queryClient.invalidateQueries({ queryKey: ["stations"] });
    }
  });
}

export function useUpdateStationPolicyMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateStationPolicyInput) => updateStationPolicy(input),
    onSuccess: (_station, input) => {
      void queryClient.invalidateQueries({ queryKey: ["station", input.stationId] });
      void queryClient.invalidateQueries({ queryKey: ["station-license-state", input.stationId] });
      void queryClient.invalidateQueries({ queryKey: ["station-audit", input.stationId] });
      void queryClient.invalidateQueries({ queryKey: ["stations"] });
    }
  });
}

export function useStationAuditQuery(stationId: string) {
  return useQuery({
    queryKey: ["station-audit", stationId],
    queryFn: () => listStationAudit(stationId),
    enabled: stationId.length > 0
  });
}

export function useAuditQuery(params: URLSearchParams, enabled = true) {
  return useQuery({
    queryKey: ["audit", params.toString()],
    queryFn: () => listAudit(params),
    enabled
  });
}

export function useRolloutReadinessReportQuery(params: URLSearchParams, enabled = true) {
  return useQuery({
    queryKey: ["report", "rollout-readiness", params.toString()],
    queryFn: () => getRolloutReadinessReport(params),
    enabled
  });
}

export function useOverrideUsageReportQuery(params: URLSearchParams, enabled = true) {
  return useQuery({
    queryKey: ["report", "override-usage", params.toString()],
    queryFn: () => getOverrideUsageReport(params),
    enabled
  });
}

export function usePublicKeyQuery() {
  return useQuery({
    queryKey: ["public-key"],
    queryFn: getPublicKey
  });
}
